/* Theme-only adapter: preserve DPO's floating panel and original handlers.
 * Remember its preferred side and measure clearance for the sticky gallery.
 * The gallery and product form never move when the overlay changes sides.
 */
(() => {
  'use strict';

  window.dmadeResponsiveBuilderCleanup?.();

  const desktop = window.matchMedia('(min-width: 990px)');
  const sideStorageKey = 'dmade-builder-summary-side';
  let preferredSide = 'right';
  try {
    const savedSide = window.localStorage.getItem(sideStorageKey);
    if (savedSide === 'left' || savedSide === 'right') preferredSide = savedSide;
  } catch (_) { /* Storage may be unavailable; the control still works. */ }
  let panel = null;
  let frame = 0;
  let stopped = false;
  let observedHeaders = [];
  let observedRoot = null;
  let sideInitialized = false;
  let syncingSide = false;

  function panelSide() {
    if (!panel || window.innerWidth < 750) return null;
    if (panel.style.left && panel.style.left !== 'auto' && panel.style.left !== '50%') return 'left';
    if (panel.style.right && panel.style.right !== 'auto') return 'right';
    return null;
  }

  function handleSideClick(event) {
    const button = event.target.closest?.('.dmade-summary-side');
    if (!button || !panel?.contains(button)) return;
    // DPO's existing button handler runs first and owns its internal side state,
    // arrow, accessible label and tablet placement. Mirror its chosen side.
    const side = panelSide();
    if (!side) return;
    preferredSide = side;
    sideInitialized = true;
    if (!syncingSide) {
      try { window.localStorage.setItem(sideStorageKey, side); } catch (_) { /* Optional persistence. */ }
    }
    queueLayout();
  }

  function syncSide() {
    const side = panelSide();
    const button = panel?.querySelector('.dmade-summary-side');
    if (!side || !button) return;
    if (!sideInitialized) {
      sideInitialized = true;
      if (side !== preferredSide) {
        // Use DPO's own control rather than overriding only its coordinates.
        syncingSide = true;
        try { button.click(); } finally { syncingSide = false; }
      }
    }
    preferredSide = panelSide() || preferredSide;
  }

  function resetPanel() {
    if (panel) {
      panel.classList.remove('dmade-summary-floating');
      panel.removeAttribute('data-dmade-summary-side');
      panel.style.removeProperty('--dmade-summary-top');
    }
  }

  function queueLayout() {
    if (!stopped && !frame) frame = requestAnimationFrame(updateLayout);
  }

  const sizeObserver = new ResizeObserver(queueLayout);
  const observer = new MutationObserver(queueLayout);

  function updateLayout() {
    frame = 0;
    if (stopped) return;

    const root = document.querySelector('#MainContent .dmade-responsive-builder');
    const rail = root?.querySelector('.product:not(.product--no-media) > .product__media-wrapper');
    const currentPanel = document.getElementById('dmade-current-options');

    if (currentPanel !== panel) {
      resetPanel();
      panel = currentPanel;
      sideInitialized = false;
    }

    const headers = [...document.querySelectorAll('#dmade-leadtime-bar, #shopify-section-header, sticky-header')];
    if (root !== observedRoot || headers.length !== observedHeaders.length || headers.some((header, index) => header !== observedHeaders[index])) {
      sizeObserver.disconnect();
      headers.forEach(header => sizeObserver.observe(header));
      observedHeaders = headers;
      observedRoot = root;
    }

    if (root && panel) syncSide();

    if (!root || !rail) {
      resetPanel();
      return;
    }

    if (panel) {
      panel.classList.toggle('dmade-summary-floating', desktop.matches);
      panel.setAttribute('data-dmade-summary-side', preferredSide);
    }

    // Reserve the lead-time banner plus a sticky/fixed header if present.
    // Full header height avoids dependence on the current scroll position.
    const banner = document.getElementById('dmade-leadtime-bar');
    const bannerHeight = banner?.getBoundingClientRect().height || 0;
    let headerHeight = 0;
    for (const header of headers) {
      if (header === banner) continue;
      const position = getComputedStyle(header).position;
      if (position === 'fixed' || position === 'sticky') {
        headerHeight = Math.max(headerHeight, header.getBoundingClientRect().height);
      }
    }
    const top = `${Math.ceil(bannerHeight + headerHeight + 16)}px`;
    if (root.style.getPropertyValue('--dmade-summary-top') !== top) {
      root.style.setProperty('--dmade-summary-top', top);
    }
    if (panel && panel.style.getPropertyValue('--dmade-summary-top') !== top) {
      panel.style.setProperty('--dmade-summary-top', top);
    }
  }

  function cleanup() {
    stopped = true;
    cancelAnimationFrame(frame);
    observer.disconnect();
    sizeObserver.disconnect();
    desktop.removeEventListener('change', queueLayout);
    window.removeEventListener('resize', queueLayout);
    document.removeEventListener('shopify:section:load', queueLayout);
    document.removeEventListener('shopify:section:unload', queueLayout);
    document.removeEventListener('click', handleSideClick);
    resetPanel();
    observedRoot?.style.removeProperty('--dmade-summary-top');
    if (window.dmadeResponsiveBuilderCleanup === cleanup) {
      delete window.dmadeResponsiveBuilderCleanup;
    }
  }

  window.dmadeResponsiveBuilderCleanup = cleanup;
  // DPO inserts its panel asynchronously and may rebuild it after an update.
  // Watch node insertions/removals only; DPO's inline style writes need no work.
  observer.observe(document.body, { childList: true, subtree: true });
  desktop.addEventListener('change', queueLayout);
  window.addEventListener('resize', queueLayout, { passive: true });
  document.addEventListener('shopify:section:load', queueLayout);
  document.addEventListener('shopify:section:unload', queueLayout);
  document.addEventListener('click', handleSideClick);
  queueLayout();
})();
